import type {
  ManagedFileRecord,
  ProjectFileManagerSnapshot,
  ProjectFolder,
  Role,
} from "../types";
import { projectFileExtension } from "./files";

export type ArchiveFilter = "active" | "archived" | "all";
export type FileSortKey = "name" | "date" | "type" | "size";
export type SortDirection = "asc" | "desc";
export type FileKind =
  | "folder"
  | "pdf"
  | "document"
  | "spreadsheet"
  | "presentation"
  | "email"
  | "image"
  | "audio"
  | "video"
  | "file";

export type ManagerItem =
  | { kind: "folder"; folder: ProjectFolder }
  | { kind: "file"; file: ManagedFileRecord };

function matchesArchiveFilter(archived: boolean, filter: ArchiveFilter) {
  return filter === "all" || (filter === "archived" ? archived : !archived);
}

export function fileKind(name: string, mimeType = ""): FileKind {
  const extension = projectFileExtension(name);
  if (extension === "pdf") return "pdf";
  if (["xls", "xlsx", "csv"].includes(extension)) return "spreadsheet";
  if (["ppt", "pptx"].includes(extension)) return "presentation";
  if (extension === "msg") return "email";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("video/")) return "video";
  if (["doc", "docx", "txt", "rtf"].includes(extension)) return "document";
  return "file";
}

export function fileTypeLabel(file: Pick<ManagedFileRecord, "name" | "mime_type">) {
  const extension = projectFileExtension(file.name);
  const labels: Record<string, string> = {
    pdf: "PDF",
    doc: "Word",
    docx: "Word",
    xls: "Excel",
    xlsx: "Excel",
    csv: "CSV",
    ppt: "PowerPoint",
    pptx: "PowerPoint",
    txt: "Texto",
    rtf: "RTF",
    msg: "Outlook MSG",
  };
  const kindLabels: Record<FileKind, string> = {
    folder: "Carpeta",
    pdf: "PDF",
    spreadsheet: "Excel",
    presentation: "PowerPoint",
    email: "Correo Outlook",
    image: "Imagen",
    audio: "Audio",
    video: "Video",
    document: "Documento",
    file: extension ? extension.toUpperCase() : "Archivo",
  };
  return labels[extension] ?? kindLabels[fileKind(file.name, file.mime_type)] ?? "Archivo";
}

export function breadcrumbs(
  folders: ProjectFolder[],
  currentFolderId: string | null,
) {
  if (!currentFolderId) return [];
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const chain: ProjectFolder[] = [];
  const seen = new Set<string>();
  let cursor = byId.get(currentFolderId);
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    chain.unshift(cursor);
    cursor = cursor.parent_folder_id
      ? byId.get(cursor.parent_folder_id)
      : undefined;
  }
  return chain;
}

export function folderDescendantIds(
  folders: ProjectFolder[],
  folderId: string,
) {
  const descendants = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const folder of folders) {
      if (
        folder.parent_folder_id &&
        (folder.parent_folder_id === folderId ||
          descendants.has(folder.parent_folder_id)) &&
        !descendants.has(folder.id)
      ) {
        descendants.add(folder.id);
        changed = true;
      }
    }
  }
  return descendants;
}

function itemName(item: ManagerItem) {
  return item.kind === "folder" ? item.folder.name : item.file.name;
}

function itemDate(item: ManagerItem) {
  return item.kind === "folder"
    ? item.folder.updated_at
    : item.file.updated_at || item.file.created_at;
}

function itemType(item: ManagerItem) {
  return item.kind === "folder"
    ? "Carpeta"
    : fileTypeLabel(item.file);
}

function itemSize(item: ManagerItem) {
  return item.kind === "folder" ? 0 : item.file.size_bytes;
}

export function sortManagerItems(
  items: ManagerItem[],
  key: FileSortKey,
  direction: SortDirection,
) {
  const multiplier = direction === "asc" ? 1 : -1;
  return [...items].sort((left, right) => {
    if (left.kind !== right.kind) return left.kind === "folder" ? -1 : 1;
    const comparison =
      key === "date"
        ? itemDate(left).localeCompare(itemDate(right))
        : key === "type"
          ? itemType(left).localeCompare(itemType(right), "es", {
              sensitivity: "base",
            })
          : key === "size"
            ? itemSize(left) - itemSize(right)
            : itemName(left).localeCompare(itemName(right), "es", {
                sensitivity: "base",
                numeric: true,
              });
    return comparison === 0
      ? itemName(left).localeCompare(itemName(right), "es", {
          sensitivity: "base",
          numeric: true,
        })
      : comparison * multiplier;
  });
}

export function currentFolderItems(
  snapshot: ProjectFileManagerSnapshot,
  currentFolderId: string | null,
  filter: ArchiveFilter,
) {
  const folders = snapshot.folders
    .filter(
      (folder) =>
        folder.parent_folder_id === currentFolderId &&
        matchesArchiveFilter(folder.effective_archived, filter),
    )
    .map((folder): ManagerItem => ({ kind: "folder", folder }));
  const files = snapshot.files
    .filter(
      (file) =>
        file.folder_id === currentFolderId &&
        matchesArchiveFilter(file.effective_archived, filter),
    )
    .map((file): ManagerItem => ({ kind: "file", file }));
  return [...folders, ...files];
}

export interface SearchResult {
  item: ManagerItem;
  location: string;
  openFolderId: string | null;
}

export function searchProjectFiles(
  snapshot: ProjectFileManagerSnapshot,
  query: string,
  filter: ArchiveFilter,
) {
  const normalized = query.trim().toLocaleLowerCase("es");
  if (!normalized) return [];
  const folderResults = snapshot.folders
    .filter(
      (folder) =>
        matchesArchiveFilter(folder.effective_archived, filter) &&
        folder.name.toLocaleLowerCase("es").includes(normalized),
    )
    .map((folder): SearchResult => ({
      item: { kind: "folder", folder },
      location: folder.name_path.slice(0, -1).join(" / ") || "Archivos",
      openFolderId: folder.id,
    }));
  const fileResults = snapshot.files
    .filter(
      (file) =>
        matchesArchiveFilter(file.effective_archived, filter) &&
        file.name.toLocaleLowerCase("es").includes(normalized),
    )
    .map((file): SearchResult => ({
      item: { kind: "file", file },
      location: file.location_path.join(" / ") || "Archivos",
      openFolderId: file.folder_id,
    }));
  return [...folderResults, ...fileResults];
}

export function canManageFile(
  role: Role,
  viewerId: string,
  file: Pick<ManagedFileRecord, "uploaded_by">,
) {
  return role !== "CLIENT_MEMBER" || file.uploaded_by === viewerId;
}

export function canManageFolder(
  snapshot: ProjectFileManagerSnapshot,
  folder: ProjectFolder,
) {
  if (snapshot.viewer_role !== "CLIENT_MEMBER") return true;
  if (folder.created_by !== snapshot.viewer_id) return false;
  const subtree = folderDescendantIds(snapshot.folders, folder.id);
  subtree.add(folder.id);
  return (
    snapshot.folders.every(
      (candidate) =>
        !subtree.has(candidate.id) || candidate.created_by === snapshot.viewer_id,
    ) &&
    snapshot.files.every(
      (file) =>
        !file.folder_id ||
        !subtree.has(file.folder_id) ||
        file.uploaded_by === snapshot.viewer_id,
    )
  );
}

export function folderMoveTargets(
  folders: ProjectFolder[],
  movingFolderId: string,
) {
  const excluded = folderDescendantIds(folders, movingFolderId);
  excluded.add(movingFolderId);
  return folders.filter(
    (folder) => !excluded.has(folder.id) && !folder.effective_archived,
  );
}
